import { driver } from 'driver.js';
import { RouteConfig } from './types';
import { hasCompletedOnboarding, markOnboardingComplete, resetOnboarding, markAllOnboardingComplete } from './utils';
import { ONBOARDING_KEYS } from './constants';
import { homeRoutes } from './routes/home';
import { monitoringRoutes } from './routes/monitoring';
import { monitoringAlertDetailRoutes } from './routes/monitoringAlertDetail';
import { alertLogRoutes } from './routes/alertLog';
import { customerRoutes } from './routes/customers';
import { siteRoutes } from './routes/sites';
import { deviceRoutes } from './routes/devices';
import { connectionRoutes } from './routes/connections';
import { userRoutes } from './routes/users';
import { teamRoutes } from './routes/teams';
import { alertAuditRoutes } from './routes/alertAudit';
import { viewerRoutes } from './routes/viewer';
import { getPreferredClient } from '@/lib/auth';
import { useAuthStore } from '@/store/authStore';

/**
 * Disable onboarding auto-trigger for the user.
 * Called when user clicks "x" button on any tour.
 * Marks all tours as complete to prevent any from auto-appearing again.
 */
async function disableOnboardingAutoTrigger(userId: string): Promise<void> {
  try {
    // Mark all tours as completed
    const allProcessNames = Object.values(ONBOARDING_KEYS);
    await markAllOnboardingComplete(userId, allProcessNames);

    // Note: announcements are suppressed for new users via useAnnouncementDelay hook
    // (24-hour delay after onboarding completes), so no need to mass-dismiss here.

    // Disable onboarding_required preference
    await getPreferredClient().post('/api/UserPreferences', {
      key: 'onboarding_required',
      value: 'false',
    });
    console.log('[Onboarding] Auto-trigger disabled - user clicked close, all tours marked complete');
  } catch (error) {
    console.error('[Onboarding] Failed to disable auto-trigger:', error);
  }
}

export { ONBOARDING_KEYS };
export {
  hasCompletedOnboarding,
  hasCompletedOnboardingAsync,
  markOnboardingComplete,
  resetOnboarding,
  syncOnboardingFromBackend,
  getOnboardingStateFromBackend,
  saveOnboardingStateToBackend,
  markWelcomeDismissed,
  hasWelcomeDismissed,
  resetAllOnboardingCompletion,
  markAllOnboardingComplete,
} from './utils';
export type { OnboardingStep, OnboardingState } from './types';

type DriverInstance = ReturnType<typeof driver>;

// Combine all routes into a single registry
const allRoutes: RouteConfig[] = [
  ...homeRoutes,
  ...monitoringRoutes,
  ...monitoringAlertDetailRoutes,
  ...alertLogRoutes,
  ...customerRoutes,
  ...siteRoutes,
  ...deviceRoutes,
  ...connectionRoutes,
  ...userRoutes,
  ...teamRoutes,
  ...alertAuditRoutes,
  ...viewerRoutes,
];

function getRouteConfig(path: string, userRoles?: string[]): RouteConfig | undefined {
  const matchingRoutes = allRoutes.filter((config) => config.match(path));
  
  if (matchingRoutes.length === 1) {
    return matchingRoutes[0];
  }
  
  // Handle multiple matching routes (e.g., home-monitoring vs home-admin)
  const isHomePage = path === '/' || path === '/home' || path === '/dashboard';
  
  if (isHomePage && userRoles) {
    const hasAdminRole = userRoles.some(role => role === 'IntegratorAdmin');
    const preferredRoute = hasAdminRole ? 'home-admin' : 'home-monitoring';
    const route = matchingRoutes.find(r => r.id === preferredRoute);
    
    if (route) {
      return route;
    }
  }
  
  // Default to first matching route
  return matchingRoutes[0];
}

function startOnboardingForRoute(
  routeConfig: RouteConfig,
  userId: string,
  currentPath?: string,
  skipCompletionCheck = false
): void {
  if (!skipCompletionCheck && hasCompletedOnboarding(routeConfig.processName, userId)) {
    console.log('[Onboarding] Route already completed, skipping', {
      processName: routeConfig.processName,
      path: currentPath,
    });
    return;
  }

  const availableSteps = routeConfig.steps.filter((step) => {
    if (typeof step.element === 'string') {
      const element = document.querySelector(step.element);
      if (!element) {
        console.log('[Onboarding] Step element not found:', step.element);
      }
      return !!element;
    }
    return false;
  });

  if (availableSteps.length === 0) {
    console.warn('[Onboarding] No steps available for route - elements not found on current page', {
      path: currentPath,
      routeId: routeConfig.id,
      processName: routeConfig.processName,
    });
    return;
  }

  // Mark onboarding as complete when tour is closed
  const markComplete = () => {
    console.log('[Onboarding] Tour closed - marking as complete', {
      processName: routeConfig.processName,
      routeId: routeConfig.id,
    });
    // Fire and forget - async call to save to backend
    markOnboardingComplete(routeConfig.processName, userId).catch((error) => {
      console.error('[Onboarding] Failed to mark complete:', error);
    });
  };

  const driverObj: DriverInstance = driver({
    showProgress: true,
    allowClose: true,
    showButtons: ['next', 'previous', 'close'],
    popoverClass: 'driverjs-popover',
    onDestroyStarted: () => {
      markComplete();
    },
    steps: availableSteps.map((step, index) => ({
      ...step,
      popover: {
        ...step.popover,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
        onCloseClick: (_element?: Element, _step?: unknown, _options?: any) => {
          disableOnboardingAutoTrigger(userId);
          driverObj.destroy();
          return true;
        },
        // Handle "Done" button on last step
         
        onNextClick: index === availableSteps.length - 1 
          ? (_element?: Element, _step?: unknown, _options?: any) => {
              markComplete();
              driverObj.destroy();
            }
          : (_element?: Element, _step?: unknown, _options?: any) => {
              driverObj.moveNext();
            },
      },
    })),
  });

  console.log('[Onboarding] Starting tour');
  
  try {
    driverObj.drive();
  } catch (error) {
    console.error('[Onboarding] Error starting tour:', error);
  }
}

export function startOnboarding(userId: string, currentPath?: string, userRoles?: string[]): void {
  const path = currentPath || window.location.pathname;
  
  // Get user roles from auth store if not provided
  const roles = userRoles || useAuthStore.getState().roles || [];
  
  const routeConfig = getRouteConfig(path, roles);

  if (!routeConfig) {
    console.warn('[Onboarding] No route configuration found for path', path);
    return;
  }

  startOnboardingForRoute(routeConfig, userId, currentPath);
}

/**
 * Restart the onboarding tour for the current route.
 * This resets the completion status and starts the tour fresh.
 * @param userId - The user's ID
 * @returns true if a tour was started, false if no route config exists for the current path
 */
export async function restartOnboardingForCurrentRoute(userId: string): Promise<boolean> {
  const path = window.location.pathname;
  
  // Get user roles from auth store
  const roles = useAuthStore.getState().roles || [];
  
  const routeConfig = getRouteConfig(path, roles);

  if (!routeConfig) {
    console.warn('[Onboarding] No route configuration found for path', path);
    return false;
  }

  console.log('[Onboarding] Restarting tour for route', {
    routeId: routeConfig.id,
    processName: routeConfig.processName,
    path,
  });

  await resetOnboarding(routeConfig.processName, userId);
  startOnboardingForRoute(routeConfig, userId, path, true);
  return true;
}

/**
 * Check if the current route has an onboarding tour available
 * @returns true if the current route has an onboarding configuration
 */
export function hasOnboardingForCurrentRoute(): boolean {
  const path = window.location.pathname;
  const roles = useAuthStore.getState().roles || [];
  return !!getRouteConfig(path, roles);
}

export { getRouteConfig, startOnboardingForRoute };
